/* eslint-disable max-lines-per-function */

import { Checkbox, InputText, TextArea } from "@bluframe/blublocks"
import Button from "components/buttons/Button"
import Typography from "components/Typography"
import { type ChangeEventHandler, useCallback } from "react"

import type { ComponentProps } from "."
import {
  Detail,
  DetailActions,
  DetailHeader,
  DetailMeta,
  DetailTitle,
  SectionTitle
} from "./styled"

type Props = Pick<
  ComponentProps,
  | "draftIsIgnored"
  | "draftManualPriority"
  | "draftNotes"
  | "isOverrideSaveDisabled"
  | "onCloseDetail"
  | "onDraftIsIgnoredChange"
  | "onDraftManualPriorityChange"
  | "onDraftNotesChange"
  | "onJustInteracted"
  | "onSaveOverrides"
  | "panelRef"
  | "selectedHandle"
>

const ProspectQueueDetail = ({
  draftIsIgnored,
  draftManualPriority,
  draftNotes,
  isOverrideSaveDisabled,
  onCloseDetail,
  onDraftIsIgnoredChange,
  onDraftManualPriorityChange,
  onDraftNotesChange,
  onJustInteracted,
  onSaveOverrides,
  panelRef,
  selectedHandle
}: Props) => {
  const handleManualPriorityChange = useCallback<
    ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      onDraftManualPriorityChange(event.target.value)
    },
    [onDraftManualPriorityChange]
  )

  if (!selectedHandle) return null

  return (
    <Detail
      aria-label="Prospect details"
      ref={panelRef}
      role="region"
      tabIndex={-1}
    >
      <DetailHeader>
        <DetailTitle>
          <Typography.H3>
            {selectedHandle.displayName ?? selectedHandle.handle}
          </Typography.H3>

          <Typography.SubtitleH3>
            <Typography.ExternalLink
              href={`https://bsky.app/profile/${encodeURIComponent(
                selectedHandle.handle
              )}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {selectedHandle.handle}
            </Typography.ExternalLink>
          </Typography.SubtitleH3>
        </DetailTitle>

        <Button label="Close" onClick={onCloseDetail} outlined />
      </DetailHeader>

      <DetailMeta>
        <Typography.SubtitleH3>{`Relationship: ${selectedHandle.relationshipState}`}</Typography.SubtitleH3>

        <Typography.SubtitleH3>{`Followers: ${selectedHandle.followerCount}`}</Typography.SubtitleH3>

        <Typography.SubtitleH3>{`Following: ${selectedHandle.followingCount}`}</Typography.SubtitleH3>

        <Typography.SubtitleH3>{`Follow-back ratio: ${selectedHandle.followBackRatio.toFixed(
          2
        )}`}</Typography.SubtitleH3>

        <Typography.SubtitleH3>{`Score: ${selectedHandle.score.toFixed(
          2
        )}`}</Typography.SubtitleH3>

        <Typography.SubtitleH3>{`Outbound amplification: ${selectedHandle.outboundAmplification.toFixed(
          2
        )}`}</Typography.SubtitleH3>

        <Typography.SubtitleH3>{`Outbound reshare rate: ${selectedHandle.outboundReshareRate.toFixed(
          2
        )}`}</Typography.SubtitleH3>
      </DetailMeta>

      <SectionTitle>{"Overrides"}</SectionTitle>

      <TextArea
        inputId="prospect-notes"
        isExpanded
        label="Notes"
        name="notes"
        onChange={onDraftNotesChange}
        rows={6}
        value={draftNotes}
      />

      <Checkbox
        inputId="prospect-ignored"
        isChecked={draftIsIgnored}
        label="Ignored"
        name="isIgnored"
        onChange={onDraftIsIgnoredChange}
        value="isIgnored"
      />

      <InputText
        inputId="prospect-manual-priority"
        label="Manual priority (0..1)"
        name="manualPriority"
        onChange={handleManualPriorityChange}
        step="0.01"
        type="number"
        value={draftManualPriority}
      />

      <DetailActions>
        <Button label="Just interacted" onClick={onJustInteracted} outlined />

        <Button
          disabled={isOverrideSaveDisabled}
          label="Save overrides"
          onClick={onSaveOverrides}
        />
      </DetailActions>
    </Detail>
  )
}

export default ProspectQueueDetail
